// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import SectionWrapper from "@flip/App/Components/Common/SectionWrapper";
import Notify from "@flip/App/Components/Notify/Notify";
import dataTableSharedStyles from "@flip/App/SharedStyle/MyAccount/List/DataTableSharedStyles";
import { colorText } from "@flip/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasPagingParameters } from "@insite/client-framework/Services/ApiService";
import { GetQuotesApiParameter } from "@insite/client-framework/Services/QuoteService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadQuotes from "@insite/client-framework/Store/Data/Quotes/Handlers/LoadQuotes";
import { getQuotesDataView } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CardContainer, { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import CardList, { CardListStyles } from "@insite/content-library/Components/CardList";
import CardListHeading from "@insite/content-library/Components/CardListHeading";
import QuoteSummaryCard, { QuoteSummaryCardStyles } from "@insite/content-library/Components/QuoteSummaryCard";
import { DataTableProps } from "@insite/mobius/DataTable";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { DataTable, DataTableBody, DataTableHead, DataTableHeader } from "@insite/mobius/index";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

// XNG-Changes
const enum fields {
    numberOfQuotes = "numberOfQuotes",
}

// XNG-Changes
interface OwnProps extends WidgetProps {
    fields: {
        [fields.numberOfQuotes]: number;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const isAuthenticated = session.isAuthenticated && !session.isGuest;
    return {
        settingsCollection: getSettingsCollection(state),
        session,
        quotesDataView: getQuotesDataView(state, recentQuotesParameter),
        myQuotesPageNavLink: getPageLinkByPageType(state, "RfqMyQuotesPage"),
        canViewQuotes: isAuthenticated && (session.userRoles || "").indexOf("Requisitioner") === -1,
    };
};

const mapDispatchToProps = {
    loadQuotes,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RecentQuotesStyles {
    cardList?: CardListStyles;
    noQuotesGridItem?: GridItemProps;
    noQuotesText?: TypographyPresentationProps;
    cardContainer?: CardContainerStyles;
    quoteSummaryCard?: QuoteSummaryCardStyles;
    // XNG-changes
    dataTable?: DataTableProps;
}

export const recentQuotesStyles: RecentQuotesStyles = {
    noQuotesGridItem: {
        ...dataTableSharedStyles.noResultsContainer,
    },
    noQuotesText: {
        ...dataTableSharedStyles.noResultsText,
    },
    dataTable: {
        ...dataTableSharedStyles.dataTable,
    },
};

const recentQuotesParameter = {
    page: 1,
    pageSize: 5,
};

const styles = recentQuotesStyles;

class RecentQuotes extends React.Component<Props> {
    componentDidMount() {
        // XNG-Changes
        recentQuotesParameter.pageSize = this.props.fields.numberOfQuotes;
        if (this.props.canViewQuotes && !this.props.quotesDataView.value) {
            this.props.loadQuotes(recentQuotesParameter);
        }
    }

    render() {
        const { settingsCollection, session, quotesDataView, myQuotesPageNavLink, canViewQuotes } = this.props;
        if (!canViewQuotes || !quotesDataView.value) {
            return null;
        }

        const myQuotesPageUrl = myQuotesPageNavLink ? myQuotesPageNavLink.url : undefined;

        // XNG-Changes: Structure, change notify
        return (
            <SectionWrapper
                title={colorText(translate("Recent Quotes"))}
                linkText={translate("View All")}
                link={myQuotesPageUrl}
                data-test-selector="widget_RecentQuotes"
            >
                {quotesDataView.value.length === 0 && (
                    <Notify type="info" showClose={false}>
                        <Typography>{siteMessage("Rfq_NoQuotesMessage")}</Typography>
                    </Notify>
                )}
                {quotesDataView.value.length > 0 && (
                    <DataTable {...styles.dataTable}>
                        <DataTableHead>
                            <DataTableHeader>{translate("Quote Number")}</DataTableHeader>
                            {/* {settingsCollection.quoteSettings.jobQuoteEnabled && (
                                <DataTableHeader>{translate("Type")}</DataTableHeader>
                            )} */}
                            <DataTableHeader>{translate("Date")}</DataTableHeader>
                            <DataTableHeader>{translate("PO #")}</DataTableHeader>
                            <DataTableHeader>{translate("Status")}</DataTableHeader>
                            {session.isSalesPerson ? (
                                <>
                                    <DataTableHeader>{translate("Sales Rep")}</DataTableHeader>
                                    <DataTableHeader>{translate("User")}</DataTableHeader>
                                </>
                            ) : (
                                <DataTableHeader>{translate("Ship To")}</DataTableHeader>
                            )}
                            {/* <DataTableHeader>{translate("Expires")}</DataTableHeader> */}
                        </DataTableHead>
                        <DataTableBody>
                            {quotesDataView.value.map(
                                quote =>
                                    quote !== null && (
                                        <QuoteSummaryCard
                                            key={quote.id}
                                            quote={quote}
                                            session={session}
                                            quoteSettings={settingsCollection.quoteSettings}
                                            extendedStyles={styles.quoteSummaryCard}
                                        />
                                    ),
                            )}
                        </DataTableBody>
                    </DataTable>
                )}
            </SectionWrapper>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RecentQuotes),
    definition: {
        group: "Common",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.numberOfQuotes,
                displayName: "Number of Recent Quotes Displayed",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 5,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
    },
};

export default widgetModule;
